import { useTypedApiClient } from './useTypedApiClient'
import { type paths } from 'api/playerPayback/api'
import type {
  ApiError,
  OpArgType,
  OpReturnType,
} from 'openapi-typescript-fetch'
import { type UseQueryOptions, useQuery } from '@tanstack/react-query'
import { useAccessTokenContext } from 'contexts/AccessTokenContext'

type HttpMethod =
  | 'get'
  | 'put'
  | 'post'
  | 'delete'
  | 'options'
  | 'head'
  | 'patch'
  | 'trace'

function isCompleteArg<
  PathT extends keyof paths,
  MethodT extends Extract<keyof paths[PathT], HttpMethod>,
>(
  arg: Partial<QueryArgsType<PathT, MethodT>>
): arg is QueryArgsType<PathT, MethodT> {
  for (const key of Object.keys(arg)) {
    if (arg[key as keyof QueryArgsType<PathT, MethodT>] === undefined) {
      return false
    }
  }

  return true
}

type QueryParamsType<
  PathT extends keyof paths,
  MethodT extends Extract<keyof paths[PathT], HttpMethod>,
> = OpArgType<paths[PathT][MethodT]>

type QueryArgsType<
  PathT extends keyof paths,
  MethodT extends Extract<keyof paths[PathT], HttpMethod>,
> = [QueryParamsType<PathT, MethodT>] extends [never]
  ? Record<string, never>
  : [QueryParamsType<PathT, MethodT>] extends [undefined]
    ? Record<string, never>
    : QueryParamsType<PathT, MethodT>

type QueryDataType<
  PathT extends keyof paths,
  MethodT extends Extract<keyof paths[PathT], HttpMethod>,
> = OpReturnType<paths[PathT][MethodT]>

type UseApiQueryParams<
  PathT extends keyof paths,
  MethodT extends Extract<keyof paths[PathT], HttpMethod>,
> = UseQueryOptions<
  QueryDataType<PathT, MethodT>,
  ApiError,
  QueryDataType<PathT, MethodT>
> & {
  enabled?: boolean
  method: MethodT
  path: PathT
  queryArgs: Partial<QueryArgsType<PathT, MethodT>>
  requestHeaders?: Record<string, string | undefined>
}

export const useApiQuery = <
  PathT extends keyof paths,
  MethodT extends Extract<keyof paths[PathT], HttpMethod>,
>({
  enabled = true,
  method,
  path,
  queryKey,
  queryArgs,
  staleTime,
  requestHeaders,
}: UseApiQueryParams<PathT, MethodT>) => {
  const { accessToken } = useAccessTokenContext()
  const apiCall = useTypedApiClient({ path, method })

  return useQuery({
    enabled: enabled && isCompleteArg(queryArgs),
    queryKey,
    queryFn: async () => {
      const headers = {
        Authorization: `Bearer ${accessToken}`,
        ...requestHeaders,
      }
      const normalizedArgs =
        Object.keys(queryArgs).length === 0
          ? undefined
          : (queryArgs as QueryParamsType<PathT, MethodT>)

      const results = await apiCall(normalizedArgs, { headers })

      return results.data as QueryDataType<PathT, MethodT>
    },
    staleTime,
  })
}
